/**
 * Session Manager Module
 *
 * Handles session persistence, token refresh, and session validation
 */

import { setPersistence, browserLocalPersistence, browserSessionPersistence } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import {
  fetchWithAuth,
  setAuthToken,
  getAuthToken,
  clearAuthToken,
  setSelectedTenantId,
  clearSelectedTenantId,
} from '@/lib/api-client';
import { handleQueryError, createAuthError } from '@/lib/error-handling';
import { logger } from '@/lib/logger';
import { User, UserTenantAccess } from '@/schemas/auth';
import { Tenant } from '@/schemas/common';

/**
 * Set Firebase auth persistence based on rememberMe preference
 */
export async function setAuthPersistence(rememberMe: boolean): Promise<void> {
  const persistence = rememberMe ? browserLocalPersistence : browserSessionPersistence;
  await setPersistence(auth, persistence);
}

/**
 * Get stored token from appropriate storage
 */
export function getStoredAuthToken(): string | null {
  const rememberMe = localStorage.getItem('rememberMe') === 'true';
  return getAuthToken(rememberMe);
}

/**
 * Check if the current Firebase ID token needs refresh
 * Firebase tokens expire after 1 hour. We'll refresh if token expires in < 5 minutes.
 */
async function shouldRefreshToken(): Promise<boolean> {
  try {
    const { currentUser } = auth;
    if (!currentUser) {
      return false; // No user, no need to refresh
    }

    // Get token result with metadata
    const tokenResult = await currentUser.getIdTokenResult();
    const expirationTime = new Date(tokenResult.expirationTime).getTime();
    const now = Date.now();
    const timeUntilExpiry = expirationTime - now;

    // Refresh if token expires in less than 5 minutes (300000 ms)
    const refreshThreshold = 5 * 60 * 1000;
    return timeUntilExpiry < refreshThreshold;
  } catch (error) {
    logger.error('Error checking token expiration:', error);
    return true; // On error, refresh to be safe
  }
}

/**
 * Store auth token and remember preference
 */
export function storeAuthToken(token: string, rememberMe: boolean): void {
  setAuthToken(token, rememberMe);
  if (rememberMe) {
    localStorage.setItem('rememberMe', 'true');
  } else {
    localStorage.removeItem('rememberMe');
  }
}

/**
 * Cache user data to avoid unnecessary API calls on page refresh
 */
function getCachedUserData(): { user: User; tenantAccess: UserTenantAccess } | null {
  try {
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const storage = rememberMe ? localStorage : sessionStorage;
    const cached = storage.getItem('cachedUserData');
    if (!cached) {
      return null;
    }

    const data = JSON.parse(cached);
    // Verify it has the expected structure
    if (data.user && data.tenantAccess) {
      return data as { user: User; tenantAccess: UserTenantAccess };
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Store user data in cache for quick retrieval on page refresh
 */
function setCachedUserData(user: User, tenantAccess: UserTenantAccess): void {
  try {
    const rememberMe = localStorage.getItem('rememberMe') === 'true';
    const storage = rememberMe ? localStorage : sessionStorage;
    storage.setItem('cachedUserData', JSON.stringify({ user, tenantAccess }));
  } catch (error) {
    logger.error('Failed to cache user data:', error);
  }
}

/**
 * Clear all auth-related storage
 */
export function clearAuthStorage(): void {
  clearAuthToken();
  clearSelectedTenantId();
  localStorage.removeItem('rememberMe');
  localStorage.removeItem('invitationTenantId');
  localStorage.removeItem('cachedUserData');
  sessionStorage.removeItem('cachedUserData');
}

/**
 * Refresh authentication state from backend
 * Automatically gets tenant if not already selected
 *
 * OPTIMIZATION: Only refreshes token from Firebase if needed (expires in < 5 minutes)
 * This prevents hitting rate limits on page refreshes in factory environments
 */
export async function refreshAuthFromBackend(options?: { forceRefresh?: boolean }): Promise<{
  user: User;
  tenantAccess: UserTenantAccess;
}> {
  const rememberMe = localStorage.getItem('rememberMe') === 'true';
  let token = getAuthToken(rememberMe);

  if (!token) {
    throw createAuthError('No authentication token found');
  }

  try {
    // Check if we need to refresh the Firebase token
    // This prevents unnecessary API calls on every page load
    const needsRefresh = await shouldRefreshToken();

    // Try to use cached data if token doesn't need refresh
    // Skip cache when forceRefresh is true (e.g., after self-permission update)
    if (!needsRefresh && !options?.forceRefresh) {
      const cachedData = getCachedUserData();
      if (cachedData) {
        logger.info('✅ Using cached user data, skipping API call');
        return cachedData;
      }
      logger.info('⚠️ No cached data found, fetching from backend');
    }

    if (needsRefresh) {
      logger.info('🔄 Token expires soon, refreshing from Firebase...');
      const { currentUser } = auth;
      if (currentUser) {
        // Force refresh to get a new token from Firebase
        token = await currentUser.getIdToken(true);
        setAuthToken(token, rememberMe);
        logger.info('✅ Token refreshed successfully');
      }
    }

    // Check if we have a stored tenant ID
    const storedTenantId = localStorage.getItem('selected_tenant_id');

    let user: User;
    let tenantAccess: UserTenantAccess;

    if (storedTenantId) {
      // We have a tenant ID - call /api/auth/me with X-Tenant-ID header
      const response = await fetchWithAuth<{
        user: User;
        tenant: UserTenantAccess;
      }>('/api/auth/me');

      ({ user, tenant: tenantAccess } = response);
    } else {
      // No tenant ID - call /api/auth/tenants to get all accessible tenants
      const response = await fetchWithAuth<{
        user: User;
        tenants: UserTenantAccess[];
      }>('/api/auth/tenants', {
        skipTenant: true, // Don't add X-Tenant-ID header for this request
      });

      const { user: fetchedUser, tenants } = response;
      user = fetchedUser;

      if (!tenants || tenants.length === 0) {
        throw createAuthError('No tenants found for this user. Please contact your administrator.');
      }

      // User only has 1 tenant - auto-select it
      [tenantAccess] = tenants;
      setSelectedTenantId(tenantAccess.tenantId);

      logger.info(`🔍 Auto-selected tenant: ${tenantAccess.name} (${tenantAccess.tenantId})`);
    }

    // Cache the fresh data for next time
    setCachedUserData(user, tenantAccess);

    return { user, tenantAccess };
  } catch (error) {
    logger.error('🔍 refreshAuthFromBackend: Error:', error);
    clearAuthToken();
    clearSelectedTenantId();
    throw handleQueryError(error, ['auth', 'refresh']);
  }
}

/**
 * Complete server-side login after OAuth or email/password authentication
 */
export async function completeServerAuthentication(options: {
  idToken: string;
  rememberMe?: boolean;
  provider?: 'google' | 'microsoft';
  tenantId?: string;
  email?: string;
}): Promise<{
  user: User;
  tenants: UserTenantAccess[];
  isFirstLogin: boolean;
  message?: string;
}> {
  const { idToken, rememberMe = false, provider, tenantId, email } = options;

  try {
    const requestBody: {
      provider?: 'google' | 'microsoft';
      rememberMe: boolean;
      tenantId?: string;
      email?: string;
    } = {
      rememberMe,
    };

    if (provider) {
      requestBody.provider = provider;
    }
    if (tenantId) {
      requestBody.tenantId = tenantId;
    }
    if (email) {
      requestBody.email = email;
    }

    const response = await fetchWithAuth<{
      user: User;
      tenants: UserTenantAccess[];
      isFirstLogin: boolean;
      message?: string;
    }>('/api/auth/login', {
      method: 'POST',
      body: requestBody,
      headers: { Authorization: `Bearer ${idToken}` },
    });

    if (!response.tenants || response.tenants.length === 0) {
      throw createAuthError('No tenants found for this user. Please contact your administrator.');
    }

    setAuthToken(idToken, rememberMe);
    setSelectedTenantId(response.tenants[0].tenantId);

    if (rememberMe) {
      localStorage.setItem('rememberMe', 'true');
    } else {
      localStorage.removeItem('rememberMe');
    }

    // Cache the user data to avoid unnecessary API calls on page refresh
    setCachedUserData(response.user, response.tenants[0]);

    return response;
  } catch (error) {
    throw handleQueryError(error, ['auth', 'server-login']);
  }
}

/**
 * Convert UserTenantAccess to Tenant type
 */
export function tenantAccessToTenant(tenantAccess: UserTenantAccess): Tenant {
  return {
    tenantId: tenantAccess.tenantId,
    name: tenantAccess.name,
    slug: tenantAccess.slug,
    domain: tenantAccess.domain,
    settings: tenantAccess.settings,
    isActive: tenantAccess.isActive,
    createdAt: tenantAccess.createdAt,
    updatedAt: tenantAccess.updatedAt,
  };
}
